import React, { useState, useCallback } from 'react'
import { Box, Container, Typography, IconButton, Tooltip } from '@mui/material'
import { ChevronLeft, ChevronRight, ContentCopy, Check, LocalOffer } from '@mui/icons-material'
import { useLocale } from 'next-intl'

import { FlightOffer } from '@/src/types/flightOffer'

interface HomepageCarouselProps {
    locale: string
    offers: FlightOffer[]
    currentIndex: number
    visibleCards: FlightOffer[]
    scrollContainerRef: React.RefObject<HTMLDivElement>
    onPrev: () => void
    onNext: () => void
    onMouseEnter: () => void
    onMouseLeave: () => void
    onOfferClick: (slug: string | undefined) => void
    onMobileScroll: () => void
}

export const HomepageCarousel: React.FC<HomepageCarouselProps> = ({
    locale,
    offers,
    currentIndex,
    visibleCards,
    scrollContainerRef,
    onPrev,
    onNext,
    onMouseEnter,
    onMouseLeave,
    onOfferClick,
    onMobileScroll
}) => {
    const isRTL = locale === 'ar'

    if (!offers || offers.length === 0) {
        return null
    }

    return (
        <Container maxWidth="lg" sx={{ px: { xs: 1, sm: 2 }, mb: 1 }}>
            <Box
                sx={{
                    position: 'relative',
                    mx: 'auto',
                    maxWidth: { xs: '100%', md: '1200px' },
                    direction: isRTL ? 'rtl' : 'ltr'
                }}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
            >
                {/* Desktop Left Arrow */}
                {!isRTL ? (
                    <DesktopArrow direction="left" onClick={onPrev}>
                        <ChevronLeft fontSize="large" />
                    </DesktopArrow>
                ) : (
                    <DesktopArrow direction="left" onClick={onNext}>
                        <ChevronRight fontSize="large" />
                    </DesktopArrow>
                )}

                {/* Mobile Slider (xs, sm, md) */}
                <MobileSlider
                    scrollContainerRef={scrollContainerRef}
                    offers={offers}
                    onScroll={onMobileScroll}
                    onOfferClick={onOfferClick}
                />

                {/* Desktop Carousel (lg and above) */}
                <DesktopCarousel visibleCards={visibleCards} currentIndex={currentIndex} onOfferClick={onOfferClick} />

                {/* Desktop Right Arrow */}
                {!isRTL ? (
                    <DesktopArrow direction="right" onClick={onNext}>
                        <ChevronRight fontSize="large" />
                    </DesktopArrow>
                ) : (
                    <DesktopArrow direction="right" onClick={onPrev}>
                        <ChevronLeft fontSize="large" />
                    </DesktopArrow>
                )}
            </Box>
        </Container>
    )
}

// Desktop Navigation Arrow
const DesktopArrow: React.FC<{
    direction: 'left' | 'right'
    onClick: () => void
    children: React.ReactNode
}> = ({ direction, onClick, children }) => (
    <IconButton
        onClick={onClick}
        sx={{
            display: { xs: 'none', lg: 'flex' },
            position: 'absolute',
            [direction]: -60,
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 20,
            bgcolor: 'common.white',
            width: 48,
            height: 48
        }}
    >
        {children}
    </IconButton>
)

// Mobile Slider Component
const MobileSlider: React.FC<{
    scrollContainerRef: React.RefObject<HTMLDivElement>
    offers: FlightOffer[]
    onScroll: () => void
    onOfferClick: (slug: string | undefined) => void
}> = ({ scrollContainerRef, offers, onScroll, onOfferClick }) => {
    const locale = useLocale()
    const isRTL = locale === 'ar'

    return (
        <Box
            ref={scrollContainerRef}
            onScroll={onScroll}
            sx={{
                display: { xs: 'flex', lg: 'none' },
                overflowX: 'auto',
                scrollSnapType: 'x mandatory',
                scrollBehavior: 'smooth',
                gap: 2,
                direction: isRTL ? 'rtl' : 'ltr',
                '&::-webkit-scrollbar': {
                    display: 'none'
                },
                scrollbarWidth: 'none',
                msOverflowStyle: 'none'
            }}
        >
            {offers.map((offer, index) => (
                <Box
                    key={`mobile-${offer.offerId}-${index}`}
                    sx={{
                        flex: '0 0 100%',
                        scrollSnapAlign: 'center',
                        minWidth: 0
                    }}
                >
                    <BannerCard offer={offer} onOfferClick={onOfferClick} />
                </Box>
            ))}
        </Box>
    )
}

// Desktop Carousel Component
const DesktopCarousel: React.FC<{
    visibleCards: FlightOffer[]
    currentIndex: number
    onOfferClick: (slug: string | undefined) => void
}> = ({ visibleCards, currentIndex, onOfferClick }) => (
    <Box
        sx={{
            display: { xs: 'none', lg: 'flex' },
            gap: 3,
            justifyContent: 'space-between',
            alignItems: 'stretch',
            transition: 'all 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
            overflow: 'hidden'
        }}
    >
        {visibleCards.map((offer, index) => (
            <Box
                key={`desktop-${offer.offerId}-${currentIndex}-${index}`}
                sx={{
                    flex: '1 1 0',
                    minWidth: 0
                }}
            >
                <BannerCard offer={offer} onOfferClick={onOfferClick} />
            </Box>
        ))}
    </Box>
)

// Banner Card Component
const BannerCard: React.FC<{
    offer: FlightOffer
    onOfferClick: (slug: string | undefined) => void
}> = ({ offer, onOfferClick }) => {
    const [copied, setCopied] = useState(false)

    const handleCopyCode = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation()
            if (offer.couponCode) {
                navigator.clipboard.writeText(offer.couponCode)
                setCopied(true)
                setTimeout(() => setCopied(false), 2000)
            }
        },
        [offer.couponCode]
    )

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                height: '100%',
                borderRadius: 2,
                overflow: 'hidden',
                border: 1,
                borderColor: 'divider',
                bgcolor: 'common.white'
            }}
        >
            {/* Banner Image */}
            <Box
                onClick={() => onOfferClick(offer.slug)}
                sx={{
                    width: '100%',
                    height: '190px',
                    aspectRatio: '16/9',
                    position: 'relative',
                    overflow: 'hidden',
                    bgcolor: 'grey.100',
                    flexShrink: 0,
                    cursor: 'pointer',
                    transition: 'transform 0.2s ease-in-out',
                    '&:hover': {
                        transform: 'scale(1.05)'
                    }
                }}
            >
                <Box
                    component="img"
                    src={offer.imageUrl}
                    alt={offer.title || 'Offer banner'}
                    sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'fill',
                        objectPosition: 'center',
                        display: 'block'
                    }}
                />
            </Box>

            {/* Banner Content */}
            <Box
                sx={{
                    bgcolor: 'common.white',
                    minHeight: '60px',
                    display: 'flex',
                    flexDirection: 'column',
                    px: 2,
                    py: 1.5,
                    flexShrink: 0,
                    gap: 1
                }}
            >
                <Typography
                    variant="body1"
                    sx={{
                        fontSize: 14,
                        color: 'text.primary',
                        textAlign: 'left',
                        lineHeight: 1.5,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        fontWeight: 500
                    }}
                >
                    {offer.description || ''}
                </Typography>

                {/* Coupon Code */}
                {offer.couponCode && (
                    <Box
                        sx={{
                            p: { xs: 0.75, md: 1 },
                            pr: { xs: 0.5, md: 0.75 },
                            borderRadius: 1,
                            border: '1px dashed',
                            borderColor: 'primary.main',
                            bgcolor: 'primary.100',
                            display: 'flex',
                            alignItems: 'center',
                            gap: { xs: 0.5, md: 1 },
                            justifyContent: 'space-between'
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, md: 1 } }}>
                            <LocalOffer sx={{ fontSize: { xs: 14, md: 16 }, color: 'primary.main' }} />
                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: 600,
                                    color: 'primary.main',
                                    fontFamily: 'monospace',
                                    letterSpacing: 1,
                                    fontSize: { xs: '0.75rem', md: '0.875rem' }
                                }}
                            >
                                {offer.couponCode}
                            </Typography>
                        </Box>
                        <Tooltip title={copied ? 'Copied!' : 'Copy code'}>
                            <IconButton
                                size="small"
                                onClick={handleCopyCode}
                                sx={{
                                    p: 0.5,
                                    color: 'primary.main',
                                    '&:hover': {
                                        bgcolor: 'primary.200'
                                    }
                                }}
                            >
                                {copied ? <Check sx={{ fontSize: 16 }} /> : <ContentCopy sx={{ fontSize: 16 }} />}
                            </IconButton>
                        </Tooltip>
                    </Box>
                )}
            </Box>
        </Box>
    )
}

export default HomepageCarousel
export type { HomepageCarouselProps }
